import { type UrlTargeting, UrlTargetingFields, UrlTargetingType } from '@openx/types/targeting/domains';
import type { TargetingFieldsValues } from '@openx/types/targeting/targeting';
import { type SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { useResetToAccountDefault } from '../../shared/hooks/useResetToAccountDefault';

type ResetToDefaultProps = {
  accountUid?: string;
  field: TargetingFieldsValues;
  initState: UrlTargeting;
  onFieldUpdate: (field: TargetingFieldsValues, newData: UrlTargeting | null) => void;
  onRemoveAll: () => void;
  updateParams: (value: SetStateAction<UrlTargeting>) => void;
};

export const useResetDomainsToAccountDefault = ({
  accountUid,
  field,
  initState,
  onFieldUpdate,
  onRemoveAll,
  updateParams,
}: ResetToDefaultProps) => {
  const {
    accountDomainTargetingOption,
    blockedDomains,
    inheritanceInfoComponent,
    shouldDisplayResetButton,
    shouldTriggerUpdate,
  } = useResetToAccountDefault({
    accountUid,
    initState,
  });

  const accountDefaultState = useMemo<UrlTargeting>(
    () => ({
      type: UrlTargetingType.blacklist,
      domain_targeting_option: accountDomainTargetingOption,
      urls: blockedDomains.length ? [...blockedDomains].sort() : undefined,
      inventorylist_id: undefined,
      [UrlTargetingFields.domain_app_inventory_operator]: undefined,
    }),
    [accountDomainTargetingOption, blockedDomains]
  );

  const handleResetToAccountDefault = useCallback((): UrlTargeting => {
    updateParams(accountDefaultState);

    return accountDefaultState;
  }, [accountDefaultState, updateParams]);

  useEffect(() => {
    if (!shouldTriggerUpdate) {
      return;
    }

    if (!blockedDomains.length) {
      onRemoveAll();
      return;
    }

    updateParams(accountDefaultState);
    onFieldUpdate(field, accountDefaultState);
  }, [accountDefaultState, blockedDomains, field, onFieldUpdate, onRemoveAll, shouldTriggerUpdate, updateParams]);

  return {
    accountDefaultState,
    handleResetToAccountDefault,
    inheritanceInfoComponent,
    shouldDisplayResetButton,
  };
};
